import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../api";
import SEO from "../components/SEO";
import AppIcon from "../components/AppIcon";
import { useI18n } from "../contexts/I18nContext";
import { normalizePublicLink } from "../utils/publicNavigation";

const CATEGORIES = [
  { name: "사무용 복합기", link: "/store/office-copier", image: "/assets/de/cat-2.webp" },
  { name: "소형 복합기", link: "/store/compact-copier", image: "/assets/de/cat-3.webp" },
  { name: "프린터", link: "/store/printer", image: "/assets/de/cat-4.webp" },
  { name: "토너 · 소모품", link: "/store/toner-supplies", image: "/assets/de/cat-5.webp" },
  { name: "고속인쇄기", link: "/store/high-speed-printer", image: "/assets/de/cat-1.webp" },
  { name: "디자인용 복합기", link: "/store/design-copier", image: "/assets/de/cat-2.webp" },
];
const SHORTCUTS = [
  { label: "복합기 렌탈", icon: "printer", to: "/rental" },
  { label: "토너 구매", icon: "toner", to: "/store/toner-supplies" },
  { label: "제품 비교", icon: "compare", to: "/compare" },
  { label: "A/S 접수", icon: "service", to: "/consultation?tab=general&type=as#consultation-form" },
];
const number = (value) => Number(String(value || 0).replace(/,/g, ""));

function Hero({ config }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const primaryHero = config?.heroes?.find((hero) => hero?.title);
  return (
    <section className="ik-home-start">
      <div className="ik-home-welcome">
        <span className="ik-home-eyebrow">우리 사무실의 좋은 시작</span>
        <h1>{primaryHero?.title || <>사무기기 고민,<br />일광에서 가볍게.</>}</h1>
        <p>{primaryHero?.subtitle || "복합기부터 토너까지, 필요한 것을 한곳에서."}</p>
        <form className="ik-home-search" role="search" onSubmit={(event) => {
          event.preventDefault();
          if (query.trim()) navigate(`/store?q=${encodeURIComponent(query.trim())}`);
          else event.currentTarget.querySelector("input")?.focus();
        }}>
          <input aria-label="모델명 또는 토너명 검색" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="모델명 · 토너명 검색" enterKeyHint="search" onKeyDown={(event) => {
            if (event.key === "Enter" && (event.nativeEvent.isComposing || event.keyCode === 229)) event.preventDefault();
          }} />
          <button type="submit" aria-label="제품 검색"><AppIcon name="search" size={23} /></button>
        </form>
      </div>
      <nav className="ik-home-shortcuts" aria-label="자주 찾는 서비스">
        {SHORTCUTS.map(({ label, icon, to }) => <Link key={icon} to={to}><span><AppIcon name={icon} size={25} /></span><strong>{label}</strong></Link>)}
      </nav>
      <Link to="/rental" className="ik-home-rental-card">
        <div><span className="ik-home-eyebrow">전국 복합기 렌탈</span><h2>설치부터 관리까지,<br />사무실에 딱 맞게.</h2><p>월 정액으로 편리하게 이용하세요.</p><strong>렌탈 알아보기 <AppIcon name="arrow" size={18} /></strong></div>
        <img src="/images/featured/apeos-c4571.png" alt="후지필름 Apeos 사무용 복합기" width="160" height="190" fetchpriority="high" />
      </Link>
      <Link to="/store/toner-supplies" className="ik-home-toner-card">
        <span className="ik-home-toner-card__icon"><AppIcon name="toner" size={29} /></span>
        <div><h2>우리 프린터에 맞는 토너 찾기</h2><p>정품 · 호환 · 재생 토너를 한눈에</p></div>
        <AppIcon name="arrow" size={20} />
      </Link>
    </section>
  );
}

function Categories({ config }) {
  const configured = (config?.categoryBar || []).filter((item) => item?.name);
  const categories = configured.length ? configured : CATEGORIES;
  return (
    <section className="ik-home-section">
      <div className="ik-section-heading"><h2>어떤 제품을 찾으세요?</h2><Link to="/store">전체 보기 <AppIcon name="arrow" size={16} /></Link></div>
      <div className="ik-home-categories">
        {categories.map((item, index) => {
          const fallback = CATEGORIES[index % CATEGORIES.length];
          const source = item.image || fallback.image;
          const image = source.startsWith("/") || /^https?:\/\//i.test(source) ? source : `/api/category-images/${encodeURIComponent(source)}`;
          return <Link key={`${item.name}-${index}`} to={normalizePublicLink(item.link, fallback.link)}>
            <span><img src={image} alt="" width="72" height="72" loading="lazy" onError={(event) => {
              if (event.currentTarget.dataset.fallback) return;
              event.currentTarget.dataset.fallback = "true";
              event.currentTarget.src = fallback.image;
            }} /></span><strong>{item.name}</strong>
          </Link>;
        })}
      </div>
    </section>
  );
}

function ProductShelf({ title, description, products, to, rental, loading, failed, onRetry }) {
  return (
    <section className="ik-home-section">
      <div className="ik-section-heading"><h2>{title}</h2><Link to={to}>전체 보기 <AppIcon name="arrow" size={16} /></Link></div>
      <p className="ik-section-description">{description}</p>
      {loading ? <div className="ik-product-skeletons" role="status" aria-label="상품을 불러오는 중"><span /><span /></div> : products.length ? (
        <div className="ik-product-shelf" tabIndex={0} role="region" aria-label={`${title}, 좌우로 스크롤하여 상품 보기`}>
          {products.map((product) => {
            const titleText = product.model_name || product.name || product.title || "제품 정보";
            const price = number(rental ? product.rental_price : product.price);
            const href = normalizePublicLink(product.product_url || `/p/${product.slug}`, to);
            const content = <>
              <div className="ik-product-shelf__image">{product.image_url ? <img src={product.image_url} alt={titleText} loading="lazy" width="160" height="150" /> : <AppIcon name="printer" size={52} />}</div>
              <div className="ik-product-shelf__brand">{product.brand || "일광오피스"}</div>
              <h3>{titleText}</h3>
              <p className={rental ? "is-rental" : ""}>{price > 0 ? `${rental ? "월 " : ""}${price.toLocaleString()}원${rental ? "~" : ""}` : "가격 문의"}</p>
              {product.stock_status === "soldout" && <span className="ik-product-shelf__stock">품절 · 재입고 대기</span>}
            </>;
            return /^https?:\/\//i.test(href) ? <a key={product.id} href={href} target="_blank" rel="noopener noreferrer">{content}</a> : <Link key={product.id} to={href}>{content}</Link>;
          })}
        </div>
      ) : <div className="ik-home-empty"><p>{failed ? "상품을 불러오지 못했어요. 잠시 후 다시 확인해 주세요." : "상품을 준비하고 있어요."}</p>{failed && <button type="button" onClick={onRetry}>다시 불러오기</button>}<Link to={to}>상품 목록 보기 <AppIcon name="arrow" size={17} /></Link></div>}
    </section>
  );
}

export default function HomePage() {
  const { t } = useI18n();
  const [config, setConfig] = useState(null);
  const [saleProducts, setSaleProducts] = useState([]);
  const [rentalProducts, setRentalProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState({ sale: false, rental: false });
  const [loadKey, setLoadKey] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const request = (url, setter, kind) => api.get(url).then(({ data }) => {
      if (!active) return;
      const products = data.products || data || [];
      setter(Array.isArray(products) ? products : []);
      setFailed((value) => ({ ...value, [kind]: false }));
    }).catch(() => { if (active) setFailed((value) => ({ ...value, [kind]: true })); });
    api.get("/api/homepage-config").then(({ data }) => { if (active) setConfig(data || null); }).catch(() => {});
    Promise.allSettled([
      request("/api/store/products?fields=list&limit=12", setSaleProducts, "sale"),
      request("/api/store/products?include_rental=true&representative=true&sale_type=rental&fields=list", setRentalProducts, "rental"),
    ]).then(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [loadKey]);

  const rentals = rentalProducts.filter((p) => number(p.rental_price) > 0 || p.sale_type === "rental" || p.sale_type === "both")
    .sort((a, b) => number(a.rental_price) - number(b.rental_price)).slice(0, 12);
  const sales = saleProducts.filter((p) => p.sale_type !== "rental" && p.sale_type !== "both" && number(p.rental_price) <= 0).slice(0, 12);
  const faqs = [1, 2, 3, 4, 5].map((i) => ({ question: t[`home_faqQ${i}`], answer: t[`home_faqA${i}`] })).filter((item) => item.question);
  const features = (config?.gridCards || []).filter((card) => card?.title);
  const retry = () => setLoadKey((key) => key + 1);

  return (
    <div className="dept-home-page ik-home-page">
      <SEO title={t.home_seoTitle} description={t.home_seoDesc} keywords="복사기, 복합기, 복사기 렌탈, 복합기 렌탈, 사무용 복사기, 복사기 가격, 복합기 가격, 복사기 추천, 복사기 판매, 토너, 프린터 렌탈, 복사기 유지보수, 일광오피스" path="/" />
      <Hero config={config} />
      <Categories config={config} />
      <ProductShelf title="우리 사무실 추천 렌탈" description="설치 · 토너 · A/S까지 함께 관리하세요." products={rentals} to="/rental" rental loading={loading} failed={failed.rental} onRetry={retry} />
      <ProductShelf title="지금 찾는 사무기기와 토너" description="필요한 제품을 비교하고 간편하게 구매하세요." products={sales} to="/store" loading={loading} failed={failed.sale} onRetry={retry} />
      {features.length > 0 && <section className="ik-home-section ik-home-features" aria-label="일광오피스 추천 안내">
        {features.map((card, index) => <Link key={index} to={normalizePublicLink(card.link, "/store")}><div><h2>{card.title}</h2>{card.subtitle && <p>{card.subtitle}</p>}<span>{card.ctaLabel || "자세히 보기"}</span></div><AppIcon name="arrow" size={20} /></Link>)}
      </section>}
      <section className="ik-home-section ik-home-help">
        <div className="ik-section-heading"><h2>어떤 제품이 좋을지 고민이라면</h2></div>
        <p>출력량과 사용 환경을 알려주세요.<br />사무실에 맞는 제품을 함께 찾아드립니다.</p>
        <Link to="/search"><AppIcon name="chat" size={21} /> AI에게 제품 물어보기 <AppIcon name="arrow" size={18} /></Link>
        <Link to="/consultation?tab=rental">맞춤 견적 상담하기 <AppIcon name="arrow" size={18} /></Link>
      </section>
      <section className="ik-home-section ik-home-faq">
        <div className="ik-section-heading"><h2>자주 묻는 질문</h2></div>
        {faqs.map(({ question, answer }) => <details key={question}><summary>{question}<span aria-hidden="true">＋</span></summary><p>{answer}</p></details>)}
      </section>
      <section className="ik-home-section ik-home-contact" aria-label="빠른 상담">
        <h2>편한 방법으로 문의하세요.</h2>
        <div><a href="tel:1600-7542"><AppIcon name="phone" size={21} /><span>전화 상담<strong>1600-7542</strong></span></a><a href="https://pf.kakao.com/_RPggG" target="_blank" rel="noopener noreferrer"><AppIcon name="chat" size={21} /><span>카카오톡<strong>채널 상담</strong></span></a></div>
        <Link to="/consultation?tab=general#consultation-form">온라인 문의 작성 <AppIcon name="arrow" size={18} /></Link>
      </section>
    </div>
  );
}
